/**
 * DI-2-2 §2.1 — position clusters: which images belong to one turbine.
 *
 * Greedy single pass in time order: a point joins the FIRST cluster whose
 * centroid is within the radius, else it opens a new cluster; the
 * centroid (arithmetic mean of lat and lon) updates incrementally. A second
 * pass merges clusters whose centroids are within the same radius, repeated
 * until nothing merges (the greedy pass can split a slow-moving start).
 * `radiusM` is the largest distance of a member from the final centroid.
 *
 * Deterministic: input order does not matter beyond ties, which break on the
 * row index. Rows without a position never enter (`no_position`, groupSet).
 */
import { haversineM } from "./geo.js";
import type { LatLon, PositionCluster } from "./types.js";

/**
 * DI-3-S42 — the radii live in `radii.ts` (CLUSTER / SEARCH / POSITION GATE);
 * `TURBINE_RADIUS_M` is gone. This image pass is now the safety net INSIDE a
 * flight (`flightGroups.ts`); the drop is grouped by flight averages.
 */
import { CLUSTER_RADIUS_M } from "./radii.js";

export interface ClusterPoint extends LatLon {
  index: number;
  /** `capturedAtMs`; points without a time sort last. */
  t?: number | undefined;
}

interface Acc {
  sumLat: number;
  sumLon: number;
  members: ClusterPoint[];
}

function centroidOf(a: Acc): LatLon {
  return {
    lat: a.sumLat / a.members.length,
    lon: a.sumLon / a.members.length,
  };
}

/** Time order, then row index — never the file name (DJI counters repeat). */
export function byTimeThenIndex(
  a: { t?: number | undefined; index: number },
  b: { t?: number | undefined; index: number },
): number {
  const ta = a.t ?? Number.POSITIVE_INFINITY;
  const tb = b.t ?? Number.POSITIVE_INFINITY;
  if (ta !== tb) return ta < tb ? -1 : 1;
  return a.index - b.index;
}

export function clusterByPosition(
  points: readonly ClusterPoint[],
  radiusM: number = CLUSTER_RADIUS_M,
): PositionCluster[] {
  const ordered = points.slice().sort(byTimeThenIndex);

  const accs: Acc[] = [];
  for (const p of ordered) {
    const hit = accs.find((a) => haversineM(centroidOf(a), p) <= radiusM);
    if (hit) {
      hit.sumLat += p.lat;
      hit.sumLon += p.lon;
      hit.members.push(p);
    } else {
      accs.push({ sumLat: p.lat, sumLon: p.lon, members: [p] });
    }
  }

  // second pass: merge clusters whose centroids are within the radius
  let merged = true;
  while (merged) {
    merged = false;
    outer: for (let i = 0; i < accs.length; i++) {
      for (let j = i + 1; j < accs.length; j++) {
        const a = accs[i];
        const b = accs[j];
        if (!a || !b) continue;
        if (haversineM(centroidOf(a), centroidOf(b)) <= radiusM) {
          a.sumLat += b.sumLat;
          a.sumLon += b.sumLon;
          a.members.push(...b.members);
          accs.splice(j, 1);
          merged = true;
          break outer;
        }
      }
    }
  }

  const out = accs.flatMap((a) => {
    const centroid = centroidOf(a);
    const members = a.members.slice().sort(byTimeThenIndex);
    const first = members[0];
    if (!first) return [];
    let radius = 0;
    for (const m of members) radius = Math.max(radius, haversineM(centroid, m));
    return {
      first,
      centroid,
      radiusM: radius,
      imageIndexes: members.map((m) => m.index),
    };
  });
  out.sort((x, y) => byTimeThenIndex(x.first, y.first));
  return out.map((c, k) => ({
    clusterId: `c${String(k + 1)}`,
    centroid: c.centroid,
    radiusM: c.radiusM,
    imageIndexes: c.imageIndexes,
  }));
}
